import { useState, useEffect } from "react";
import { Checkbox } from "@mui/material";

/**
 * Reusable hook for bulk selection functionality in list pages
 * @param {Array} data - Array of data items (should have _id property)
 * @param {Function} onSelectionChange - Optional callback when selection changes
 * @param {Array} dependencies - Dependencies array to reset selection (e.g., [currentPage, pageSize])
 * @returns {Object} Selection state and handlers
 */
export const useBulkSelect = (data = [], onSelectionChange = null, dependencies = []) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Reset selection when dependencies change
  useEffect(() => {
    setSelectedRows([]);
    setSelectAll(false);
  }, dependencies);

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = data.map((row) => row._id);
      setSelectedRows(allIds);
      setSelectAll(true);
      if (onSelectionChange) onSelectionChange(allIds);
    } else {
      setSelectedRows([]);
      setSelectAll(false);
      if (onSelectionChange) onSelectionChange([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows((prev) => {
      let newSelection;
      if (prev.includes(id)) {
        newSelection = prev.filter((rowId) => rowId !== id);
        setSelectAll(false);
      } else {
        newSelection = [...prev, id];
        if (newSelection.length === data.length) {
          setSelectAll(true);
        }
      }
      if (onSelectionChange) onSelectionChange(newSelection);
      return newSelection;
    });
  };

  const clearSelection = () => {
    setSelectedRows([]);
    setSelectAll(false);
    if (onSelectionChange) onSelectionChange([]);
  };

  // Get checkbox column configuration
  const getCheckboxColumn = () => ({
    name: (
      <Checkbox
        checked={selectAll && data.length > 0}
        indeterminate={selectedRows.length > 0 && selectedRows.length < data.length}
        onChange={handleSelectAll}
      />
    ),
    width: "60px",
    cell: (row) => (
      <Checkbox
        checked={selectedRows.includes(row._id)}
        onChange={() => handleSelectRow(row._id)}
      />
    ),
    sortable: false,
    center: true,
  });

  return {
    selectedRows,
    selectAll,
    handleSelectAll,
    handleSelectRow,
    clearSelection,
    setSelectedRows,
    getCheckboxColumn,
  };
};

